require('./lineChartWarrantHistorical.scss');

const PropTypes = require('prop-types');

// @vendors
const React= require('react');
const moment = require('moment');
const d3 = require('d3');
const isEqual = require('lodash/lang/isEqual');
const reduce = require('lodash/collection/reduce');

// @helpers
const { axisDateLabel,
        yAxisFormatLabel,
        transformToISO,
        dateTicks
    } = require('./lineChartWarrantHistoricalHelpers.js');

const blackBox = props => {

    const {dataset, width, height} = props;
    const PADDING = 40;
    const Y_TICKS_NUMBER = 4;

    return me => {
        if (!dataset || !dataset.size) {
            return;
        }

        const xaxisG = me.select('.line-chart-warrant-historical__xaxis');
        const yaxisG = me.select('.line-chart-warrant-historical__yaxis');
        const gridG = me.select('.line-chart-warrant-historical__grid');
        const lineG = me.select('.line-chart-warrant-historical__line');
        const areaG = me.select('.line-chart-warrant-historical__area');

        const currency = dataset.get('currencyCode');
        const dataArray = dataset.get('historical').toJS();


        const maxDate = d3.max(dataArray, d => {
            let date = d.date;
            if (d.date.indexOf('/') !== -1) {
                date = transformToISO(d.date);
            }
            return moment.utc(date)
        }) || moment();
        const minDate = d3.min(dataArray, d => {
            let date = d.date;
            if (d.date.indexOf('/') !== -1) {
                date = transformToISO(d.date);
            }
            return moment.utc(date)
        }) || moment();
        const maxVal = d3.max(dataArray, d => +d.close);
        const minVal = d3.min(dataArray, d => +d.close);
        const xDomain = [ minDate.startOf('month'), maxDate.endOf('month') ];
        const yDomain = [ minVal, maxVal ];
        const xAxisFormat = axisDateLabel();
        const yAxisFormat = (d) => yAxisFormatLabel(d, currency);
        const LEFT_PADDING = maxVal ? 25 + maxVal.toString().length * 9 : 0;
        const VERTICAL_AXIS_TEXT_PADDING = 35;

        const xTicksNumber = dateTicks(minDate, maxDate);

        const yScale = d3.scale.linear()
            .domain(yDomain)
            .range([height - PADDING, PADDING]);

        const xScale = d3.time.scale()
            .domain(xDomain)
            .range([LEFT_PADDING * 2, width]);

        const yAxis = d3.svg.axis()
            .orient('left')
            .scale(yScale)
            .tickSize(0)
            .ticks(Y_TICKS_NUMBER)
            .outerTickSize(0)
            .tickFormat(yAxisFormat);

        const xAxis = d3.svg.axis()
            .orient('bottom')
            .scale(xScale)
            .tickSize(0)
            .ticks(xTicksNumber)
            .outerTickSize(0)
            .tickFormat(xAxisFormat);

        const lineArray = dataArray.map(item => {
            let date = item.date;
            if (item.date.indexOf('/') !== -1) {
                date = transformToISO(item.date);
            }
            return [xScale(moment.utc(date)), yScale(item.close)];
        });

        const drawLine = d3.svg.line()
                .interpolate('linear');

        const prepareTransition = d3.svg.line()
                .y(() => { return height - PADDING; })
                .interpolate('linear');

        gridG.selectAll('line').remove();
        areaG.selectAll('path').remove();
        lineG.selectAll('path').remove();

        let line = lineG.append('path')
                .datum(lineArray);

        line.attr('class', 'line')
            .attr('d', prepareTransition)
            .transition().duration(1000)
            .attr('d', drawLine);

        xScale.ticks(xTicksNumber).forEach(tick => {
            gridG.append('svg:line')
              .attr({
                x1: xScale(tick),
                x2: xScale(tick),
                y1: yScale(yDomain[0]),
                y2: yScale(yDomain[1]),
                'stroke-dasharray': ("2, 3")
              })
              .classed('line-chart-warrant-historical__horizontal-grid', true);
        });

        yScale.ticks(Y_TICKS_NUMBER).forEach(tick => {
            gridG.append('svg:line')
              .attr({
                x1: xScale(xDomain[0]),
                x2: xScale(xDomain[1]),
                y1: yScale(tick),
                y2: yScale(tick),
                'stroke-dasharray': ("2, 3")
              })
              .classed('line-chart-warrant-historical__horizontal-grid', true);
        });

        yaxisG.attr('class', 'line-chart-warrant-historical__yaxis line-chart-warrant-historical__axis')
            .attr('transform', 'translate('+ (LEFT_PADDING * 2) +',0)')
            .call(yAxis);

        xaxisG.attr('class', 'line-chart-warrant-historical__xaxis line-chart-warrant-historical__axis')
            .attr('transform', 'translate(0,' + (height - PADDING) + ')')
            .call(xAxis);

        xaxisG.selectAll('.line-chart-warrant-historical__xaxis text')
            .attr('transform', 'translate(0, 10)');

        yaxisG.selectAll('.line-chart-warrant-historical__yaxis text')
            .attr('transform', `translate(-${LEFT_PADDING - VERTICAL_AXIS_TEXT_PADDING}, 0)`);

    }
}

class LineChartWarrantHistorical extends React.Component {

    constructor(props) {
        super(props);
        this.setNode = this.setNode.bind(this);
    }

    componentDidMount() {
        d3.select(this.nodeReference)
            .call(blackBox(this.props));
    }

    componentDidUpdate(old) {
        let difference = reduce(this.props, (result, value, key) => {
            return isEqual(value, old[key]) ? result : result.concat(key);
        }, []);

        if (difference.length === 0) { return; }

        d3.select(this.nodeReference)
            .call(blackBox(this.props));
    }

    setNode(ref) {
        this.nodeReference = ref;
    }

    render() {
        return (
            <div ref={this.setNode} className="line-chart-warrant-historical">

                <svg width={this.props.width}
                    height={this.props.height}
                    shapeRendering="geometricPrecision"
                >
                    <g className="line-chart-warrant-historical__grid"></g>
                    <g className="line-chart-warrant-historical__xaxis"></g>
                    <g className="line-chart-warrant-historical__yaxis"></g>
                    <g className="line-chart-warrant-historical__line">
                        <path></path>
                    </g>
                </svg>
            </div>
        );
    }
}

LineChartWarrantHistorical.propTypes = {
    dataset: PropTypes.object.isRequired,
    width: PropTypes.string.isRequired,
    height: PropTypes.string.isRequired
};

module.exports = LineChartWarrantHistorical;